import { useCallback, useState } from "react";
import Box from "/app/src/components/generic/components/box";
import HeroDivider from "/app/src/components/HeroUi/Divider";
import { Integration, Material } from "/app/src/models";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import { useQuery } from "@tanstack/react-query";
import { materialService } from "/app/src/services";
import { ScrollShadow } from "@heroui/react";
import { useTranslation } from "react-i18next";
import useSetting from "/app/src/components/settings/setting";
import { useDebounce } from "/app/src/hooks";
import { buildParams } from "/app/src/helpers";
import DebouncedSearch from "/app/src/components/generic/components/debouncedSearch";
import HeroSpin from "/app/src/components/HeroUi/Spin";
import { materialFieldOptions } from "/app/src/constants/types";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";

/**
 * Displays material list and details for a given integration.
 * @param {Object} props - Component props.
 * @param {Integration} props.integration - The integration to fetch materials for.
 * @returns {JSX.Element|null} The rendered component or null if no material fields are configured.
 */
export default function MaterialInformation({
  integration,
}: {
  integration: Integration;
}) {
  const MATERIAL_LIST_HEIGHT = "max-h-72";
  const [searchString, setSearchString] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(
    null,
  );
  const debouncedSearchTerm = useDebounce(searchString, 1000);
  const { t } = useTranslation();
  const { settingValue: materialFields } = useSetting({
    integrationId: integration.id,
    type: "materialFields",
  });

  const { data: materials, isFetching } = useQuery({
    queryKey: ["materials", debouncedSearchTerm],
    queryFn: () =>
      materialService.getAll(
        buildParams({
          search: debouncedSearchTerm,
        }),
      ),
    initialData: { materials: [] },
    select: (data: { materials: Material[] }) => data.materials,
  });

  /**
   * Handles click on a material, toggling its selection.
   * @param {Material} material - The material to select or deselect.
   * @returns {() => void} A callback that toggles the selected material.
   */
  const onMaterialClick = useCallback((material: Material) => {
    return () => {
      setSelectedMaterial(material);
    };
  }, []);

  const fields = (materialFields || "")
    .split(",")
    .map((f) => f.trim())
    .filter(Boolean);

  /**
   * Displays a label and value in a styled box.
   * @param {string} label - The label to display.
   * @param {string|null|undefined} [value] - The value corresponding to the label.
   * @returns {JSX.Element} The rendered InfoLabel component.
   */
  const InfoLabel = ({
    label,
    value,
  }: {
    label: string;
    value?: string | null;
  }) => (
    <div className="h-[40px] px-[8px] py-[6px] flex flex-col justify-center">
      <h3 className="!mb-0 text-[12px]">{label}</h3>
      <p className="!mb-0 text-[14px]">{value ?? "-"}</p>
    </div>
  );

  return (
    <Box className="mt-[20px]">
      <h2 className="text-[18px]">{t("translation:material_lookup")}</h2>
      <HeroDivider className="my-3" />
      <div className="w-[865px]">
        <DebouncedSearch setSearchString={setSearchString} />
      </div>

      {isFetching ? (
        <div className="h-72 flex justify-center items-center">
          <HeroSpin />
        </div>
      ) : materials.length === 0 ? (
        <h3 className="mt-6 h-72 text-[24px] flex justify-center items-center">
          {t("translation:no_materials_found")}
        </h3>
      ) : (
        <GridWrapper className="mt-6" responsive={{ sm: 1, md: 2, lg: 2 }}>
          <ScrollShadow className={`${MATERIAL_LIST_HEIGHT} overflow-y-auto`}>
            {materials.map((material) => {
              const isSelected = selectedMaterial?.id === material.id;
              return (
                <div
                  className={`h-[55px] px-[16px] py-[12px] rounded text-[16px] flex items-center justify-between hover:cursor-pointer ${
                    isSelected ? "bg-info-accent" : "hover:bg-info-muted"
                  }`}
                  key={material.id}
                  onClick={onMaterialClick(material)}
                  onKeyDown={onMaterialClick(material)}
                  role="button"
                  tabIndex={0}
                >
                  <span>{material.name}</span>

                  <div className="flex flex-col text-right">
                    <span className="text-sm">{t("translation:in_stock")}</span>
                    <span className="font-medium">
                      {material.currentQuantity}
                    </span>
                  </div>
                </div>
              );
            })}
          </ScrollShadow>

          {materialFields === undefined || materialFields.length === 0 ? (
            <div className="flex flex-col space-y-4 justify-center items-center">
              <IconBuilder icon="Warning" color="#d0021b" size={32} />
              <span>{t("translation:material_lookup_warning")}</span>
            </div>
          ) : selectedMaterial ? (
            <GridWrapper
              key={selectedMaterial.id}
              responsive={{ sm: 1, md: 2, lg: 2 }}
            >
              {fields.map((field) => {
                const value = selectedMaterial[field as keyof Material];
                const label = materialFieldOptions.find(
                  (option) => option.value === field,
                )?.label;
                return (
                  <InfoLabel
                    key={field}
                    label={label ?? field}
                    value={value === null ? "-" : value?.toString()}
                  />
                );
              })}
            </GridWrapper>
          ) : null}
        </GridWrapper>
      )}
    </Box>
  );
}
